import { useState, useEffect, useRef, useCallback } from 'react';
import { getProjectAccess } from '../api/access';
import type { ProjectAccess } from '../api/access';

/**
 * Load the current user's effective access for a project.
 *
 * Fails closed: `access` stays null while loading and on error, so permission
 * helpers deny by default rather than briefly offering actions that would 403.
 * Callers should surface `error` instead of rendering a silently reduced page.
 */
export const useProjectAccess = (projectId?: string) => {
  const [access, setAccess] = useState<ProjectAccess | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Only the newest request may write state — switching projects mid-flight
  // must not be overwritten by the previous project's response.
  const requestRef = useRef(0);

  const loadAccess = useCallback(async () => {
    const requestId = ++requestRef.current;

    if (!projectId) {
      setAccess(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getProjectAccess(projectId);
      if (requestRef.current === requestId) setAccess(data);
    } catch (err) {
      if (requestRef.current === requestId) {
        // Deny by default — never fall back to guessing from the role name.
        setAccess(null);
        setError(err instanceof Error ? err.message : 'Failed to load permissions');
      }
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  return { access, loading, error, reloadAccess: loadAccess };
};
